import React, { Fragment } from 'react';
import { bool, string, arrayOf, func, number } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import classNames from 'classnames';
import { find, map, pathOr, type } from 'ramda';

import {
  required,
  bookingDateRequired,
  composeValidators,
  numberOfPeopleEnvisaged,
  numberOfPeopleEnvisagedMinimum,
  suitableForChildrenValidator,
} from '../../util/validators';
import {
  firstPartDayHour,
  secondPartDayHour,
  thirdPartDayHour,
  moment,
  slotIsAvailable,
} from '../../util/dates';
import { propTypes } from '../../util/types';
import config from '../../config';
import {
  Form,
  PrimaryButton,
  FieldDateInput,
  FieldNumericStepper,
  FieldTextInput,
  SecondaryButton,
  FieldCheckboxProduct,
  FieldSelect,
} from '../../components';
import EstimatedBreakdownMaybe from './EstimatedBreakdownMaybe';

import css from './BookingDatesForm.css';
import * as validators from '../../util/validators';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';

const { Money } = sdkTypes;
const morningSlot = 'morning';
const afternoonSlot = 'afternoon';
const eveningSlot = 'evening';
const fullDaySlot = 'fullDay';

const getBookingSlotData = (bookingSlotKey) => {
  switch (bookingSlotKey) {
    case fullDaySlot:
      return {
        hour: 0,
        isBookingFullDay: true,
      };
    case morningSlot:
      return {
        hour: firstPartDayHour,
        isBookingFullDay: false,
      };
    case afternoonSlot:
      return {
        hour: secondPartDayHour,
        isBookingFullDay: false,
      };
    default:
      return {
        hour: thirdPartDayHour,
        isBookingFullDay: false,
      };
  }
};
const priceData = (price, intl) => {
  if (price && price.currency === config.currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};
const BookingDatesFormComponent = (props) => {
  const {
    rootClassName,
    className,
    price: unitPrice,
    onSubmit,
    onSubmitEnquiry,
    listing,
    listingId,
    ...rest
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  if (!unitPrice) {
    return (
      <div className={classes}>
        <p className={css.error}>
          <FormattedMessage id='BookingDatesForm.listingPriceMissing' />
        </p>
      </div>
    );
  }
  if (unitPrice.currency !== config.currency) {
    return (
      <div className={classes}>
        <p className={css.error}>
          <FormattedMessage id='BookingDatesForm.listingCurrencyInvalid' />
        </p>
      </div>
    );
  }

  return (
    <FinalForm
      {...rest}
      onSubmit={(inputValues) => {
        const { isOnlyContact } = props;
        const { bookingDate, bookingSlot } = inputValues;
        const date = bookingDate.date;
        const { isBookingFullDay, hour: bookingDateHour } = getBookingSlotData(bookingSlot);
        const startDate = date && moment(date).set({
          hour: bookingDateHour,
          minute: 0,
        }).toISOString();
        const data = {
          ...inputValues,
          bookingDate: { date: startDate },
          isBookingFullDay,
        };
        if (isOnlyContact) {
          onSubmitEnquiry(data);
          return;
        }

        onSubmit(data);
      }}
      unitPrice={unitPrice}
      render={fieldRenderProps => {
        const {
          bookingDatePlaceholder,
          handleSubmit,
          intl,
          submitButtonWrapperClassName,
          unitPrice,
          unitType,
          values,
          timeSlots,
          fetchTimeSlotsError,
          maxCapacityToGuest,
          minCapacityToGuest,
          isBook,
          showContactUser,
          onContactUser,
          form,
          authorDisplayName,
          isOnlyContact,
          barbecueFee,
          jacuzziFee,
          towelsFee,
          snackFee,
          suitableForChildren,
        } = fieldRenderProps;
        const messageLabel = intl.formatMessage(
          {
            id: 'EnquiryForm.messageLabel',
          },
          { authorDisplayName },
        );
        const messagePlaceholder = intl.formatMessage(
          {
            id: 'EnquiryForm.messagePlaceholder',
          },
          { authorDisplayName },
        );
        const messageRequiredMessage = intl.formatMessage({
          id: 'EnquiryForm.messageRequired',
        });
        const messageRequired = validators.requiredAndNonEmptyString(messageRequiredMessage);

        const or = (!isBook && showContactUser) && (
          <div className={css.or}>
            <FormattedMessage id='ListingPage.or' />
          </div>
        );
        
        const contactUserButton = (!isBook && showContactUser) && (
          <SecondaryButton
            type='button'
            onClick={() => {
              const {
                bookingDate,
                numberOfAdults,
                numberOfChildren,
                numberOfInfants,
              } = pathOr({}, ['values'], form.getState());
              const { invalid } = form.getState();
              if (invalid) {
                form.blur('bookingDate');
                form.blur('numberOfAdults');
                return;
              }

              onContactUser(bookingDate, numberOfAdults, numberOfChildren, numberOfInfants, isBookingFullDay);
            }}
          >
            <FormattedMessage id='ListingPage.contactUser' />
          </SecondaryButton>
        );

        const {
          bookingDate = {},
          bookingSlot,
          numberOfAdults,
          numberOfChildren = 0,
          numberOfInfants = 0,
        } = values;
        const date = bookingDate.date;
        const { isBookingFullDay, hour: bookingDateHour } = getBookingSlotData(bookingSlot);
        const startDate = date && moment(date).set({
          hour: bookingDateHour,
          minute: 0,
        }).toISOString();
        // default missing number of children to 0
        const defaultedNumberOfChildren = !Number.isInteger(numberOfChildren) ? 0 : numberOfChildren;
        // default missing number of infants to 0
        const defaultednumberOfInfants = !Number.isInteger(numberOfInfants)
          ? 0
          : numberOfInfants;
        const morningSlotAvailable = date && slotIsAvailable(startDate, firstPartDayHour, timeSlots);
        const afternoonSlotAvailable = date && slotIsAvailable(startDate, secondPartDayHour, timeSlots);
        const eveningSlotAvailable = date && slotIsAvailable(startDate, thirdPartDayHour, timeSlots);
        const hasBookingInfo =
          startDate && bookingSlot && numberOfAdults && Number.isInteger(defaultedNumberOfChildren) && Number.isInteger(defaultednumberOfInfants);
        const selectedBarbecueFee = find(i => i === 'barbecueFee', pathOr([], ['additionalItems'], values)) && barbecueFee;
        const selectedTowelsFee = find(i => i === 'towelsFee', pathOr([], ['additionalItems'], values)) && towelsFee;
        const selectedSnackFee = find(i => i === 'snackFee', pathOr([], ['additionalItems'], values)) && snackFee;
        const selectedJacuzziFee = find(i => i === 'jacuzziFee', pathOr([], ['additionalItems'], values)) && jacuzziFee;
        // for customised payment processes.
        const bookingData = hasBookingInfo
          ? {
            unitType,
            unitPrice,
            startDate,
            quantity1: numberOfAdults,
            quantity2: defaultedNumberOfChildren,
            quantity3: defaultednumberOfInfants,
            isBookingFullDay,
            barbecueFee: selectedBarbecueFee,
            towelsFee: selectedTowelsFee,
            snackFee: selectedSnackFee,
            jacuzziFee: selectedJacuzziFee,
          }
          : null;
        const timeSlotsError = fetchTimeSlotsError ? (
          <p className={css.timeSlotsError}>
            <FormattedMessage id='BookingDatesForm.timeSlotsError' />
          </p>
        ) : null;

        const bookingInfo = bookingData ? (
          <div className={css.priceBreakdownContainer}>
            <h3 className={css.priceBreakdownTitle}>
              <FormattedMessage id='BookingDatesForm.priceBreakdownTitle' />
            </h3>
            <EstimatedBreakdownMaybe bookingData={bookingData} listing={listing} />
          </div>
        ) : null;

        const dateFormatOptions = {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        };

        const now = moment();
        const today = now.startOf('day').toDate();

        const dateLabel = intl.formatMessage({ id: 'BookingDatesForm.dateTitle' });
        const datePlaceholderText =
          bookingDatePlaceholder || intl.formatDate(today, dateFormatOptions);
        const dateRequiredMessage = intl.formatMessage({ id: 'BookingDatesForm.requiredDate' });
        const dateErrorMessage = intl.formatMessage({
          id: 'FieldDateInput.invalidDate',
        });

        const adultsLabel = intl.formatMessage({ id: 'BookingDatesForm.adultsLabel' });
        const adultsHelper = intl.formatMessage({ id: 'BookingDatesForm.adultsHelper' });
        const childrenLabel = intl.formatMessage({ id: 'BookingDatesForm.childrenLabel' });
        const childrenHelper = intl.formatMessage({ id: 'BookingDatesForm.childrenHelper' });
        const infantsLabel = intl.formatMessage({ id: 'BookingDatesForm.infantsLabel' });
        const infantsHelper = intl.formatMessage({ id: 'BookingDatesForm.infantsHelper' });

        const peopleRequiredMessage = intl.formatMessage({
          id: 'BookingDatesForm.peopleRequired',
        });
        const peopleRestrictedByMaxCapacityToGuestMessage = intl.formatMessage({
          id: 'BookingDatesForm.peopleRestrictedByMaxCapacityToGuest',
        }, { maxCapacityToGuest });
        const peopleRestrictedByMinCapacityToGuestMessage = intl.formatMessage({
          id: 'BookingDatesForm.peopleRestrictedByMinCapacityToGuest',
        }, { minCapacityToGuest });
        const suitableForChildrenMessage = intl.formatMessage({
          id: 'BookingDatesForm.suitableForChildren',
        });
        const submitButtonClasses = classNames(
          submitButtonWrapperClassName || css.submitButtonWrapper,
        );
        const extrasLabel = intl.formatMessage({
          id: 'BookingDatesForm.extrasLabel',
        });
        const barbecueFeeLabel = intl.formatMessage({
          id: 'BookingDatesForm.barbecueFee',
        });
        const barbecueExtraInformation = intl.formatMessage({
          id: 'BookingDatesForm.extra.barbecue.information',
        });
        const towelsFeeLabel = intl.formatMessage({
          id: 'BookingDatesForm.towelsFee',
        });
        const towelsExtraInformation = intl.formatMessage({
          id: 'BookingDatesForm.extra.towels.information',
        });
        const snackFeeLabel = intl.formatMessage({
          id: 'BookingDatesForm.snackFee',
        });
        const snackExtraInformation = intl.formatMessage({
          id: 'BookingDatesForm.extra.snack.information',
        });
        const jacuzziFeeLabel = intl.formatMessage({
          id: 'BookingDatesForm.jacuzziFee',
        });
        const jacuzziExtraInformation = intl.formatMessage({
          id: 'BookingDatesForm.extra.jacuzzi.information',
        });
        const listingHasExtras = barbecueFee || towelsFee || snackFee || jacuzziFee;
        const priceText = intl.formatMessage({ id: isBookingFullDay ? 'BookingDatesForm.fullDayPriceTitle' : 'BookingDatesForm.halfDayPriceTitle' });
        const priceHelper = intl.formatMessage({ id: 'BookingDatesForm.priceHelper' });
        const acceptChildren = type(suitableForChildren) === 'Boolean' ? suitableForChildren : true;
        const morningSlotText = intl.formatMessage({ id: 'BookingDatesForm.morningSlot' });
        const afernoonSlotText = intl.formatMessage({ id: 'BookingDatesForm.afternoonSlot' });
        const eveningSlotText = intl.formatMessage({ id: 'BookingDatesForm.eveningSlot' });
        const fullDaySlotText = intl.formatMessage({ id: 'BookingDatesForm.fullDaySlot' });
        const availableSlots = bookingDate.date
          ? [
            ...(morningSlotAvailable ? [{ key: morningSlotText, value: morningSlot }] : []),
            ...(afternoonSlotAvailable ? [{
              key: afernoonSlotText,
              value: afternoonSlot,
            }] : []),
            ...(eveningSlotAvailable ? [{ key: eveningSlotText, value: eveningSlot }] : []),
            ...(morningSlotAvailable && afternoonSlotAvailable ? [{
              key: fullDaySlotText,
              value: fullDaySlot,
            }] : []),
          ]
          : null;
        const bookingSlotOptions = availableSlots ? map(slot => (
          <option key={slot.key} value={slot.value}>{slot.key}</option>
        ), availableSlots) : null;
        const bookingSlotLabel = intl.formatMessage({ id: 'BookingDatesForm.bookingSlotLabel' });
        const bookingSlotPlaceholder = intl.formatMessage({ id: 'BookingDatesForm.bookingSlotPlaceholder' });
        const bookingSlotRequired = intl.formatMessage({ id: 'BookingDatesForm.bookingSlotRequired' });
        const fullDayPriceAmount = pathOr(null, ['attributes', 'publicData', 'fullDayPrice'], listing);
        const fullDayPriceMoney = fullDayPriceAmount ? new Money(fullDayPriceAmount, config.currency) : new Money(unitPrice.amount * 2, config.currency);
        const priceFormatted = isBookingFullDay ? ( priceData(fullDayPriceMoney, intl).formattedPrice ) : priceData(unitPrice, intl).priceTitle;
        const priceTitle = isBookingFullDay ? (priceData(fullDayPriceMoney, intl).priceTitle) : priceData(unitPrice,intl);priceTitle;
        const getMinAdults = () => {
          const possibleMin = minCapacityToGuest - numberOfChildren;
          return possibleMin > 1 ? possibleMin : 1;
        };
        const getMinChildren = () => {
          const totalParticipants = numberOfAdults + numberOfChildren;
          const minChildren = totalParticipants === minCapacityToGuest ? numberOfChildren : 0;
          return (minChildren); 
        }

        return (
          <Form className={className} onSubmit={handleSubmit}>
            {timeSlotsError}
            <FieldDateInput
              rootClassName={css.bookingDates}
              name='bookingDate'
              id='bookingDate'
              label={dateLabel}
              placeholderText={datePlaceholderText}
              format={null}
              timeSlots={timeSlots}
              useMobileMargins
              validate={composeValidators(
                required(dateRequiredMessage),
                bookingDateRequired(dateErrorMessage),
              )}
            />
            {availableSlots ? (
              <FieldSelect
                className={css.bookingSlot}
                id='bookingSlot'
                name='bookingSlot'
                label={bookingSlotLabel}
                validate={required(bookingSlotRequired)}
              >
                <option value=''>{bookingSlotPlaceholder}</option>
                {bookingSlotOptions}
              </FieldSelect>
            ) : null}             
            <div className={css.priceContainer} title={priceTitle}>
              <div className={css.priceTitleContainer}>
                <span className={css.priceTitle}>{priceText}</span>
                <span className={css.priceHelper}>{priceHelper}</span>
              </div>
                <span className={css.price}>{priceFormatted}</span>
             </div>
            <div className={css.participantsContainer}>
              <FieldNumericStepper
                className={css.selectPeople}
                min={getMinAdults()}
                name='numberOfAdults'
                id='numberOfAdults'
                key={`numberOfAdults_${numberOfChildren}`}
                label={adultsLabel}
                helper={adultsHelper}
                validate={composeValidators(
                  required(peopleRequiredMessage),
                  numberOfPeopleEnvisaged(peopleRestrictedByMaxCapacityToGuestMessage, numberOfChildren, maxCapacityToGuest),
                  numberOfPeopleEnvisagedMinimum(peopleRestrictedByMinCapacityToGuestMessage, numberOfChildren, minCapacityToGuest),
                )}
              />
              {acceptChildren ? (
                <Fragment>
                  <FieldNumericStepper
                    className={css.selectPeople}
                    name='numberOfChildren'
                    id='numberOfChildren'
                    key={`numberOfChildren_${numberOfAdults}`}
                    label={childrenLabel}
                    helper={childrenHelper}
                    min={getMinChildren()}
                    validateFields
                    validate={composeValidators(
                      suitableForChildrenValidator(suitableForChildrenMessage, acceptChildren),
                      numberOfPeopleEnvisaged(peopleRestrictedByMaxCapacityToGuestMessage, numberOfAdults, maxCapacityToGuest),
                      numberOfPeopleEnvisagedMinimum(peopleRestrictedByMinCapacityToGuestMessage, numberOfAdults, minCapacityToGuest),
                    )}
                  />
                  <FieldNumericStepper
                    className={css.selectPeople}
                    name='numberOfInfants'
                    id='numberOfInfants'
                    key='numberOfInfants'
                    label={infantsLabel}
                    helper={infantsHelper}
                    min={0}
                    validateFields
                    validate={composeValidators(
                      suitableForChildrenValidator(suitableForChildrenMessage, acceptChildren),
                    )}
                  />
                  <div className={css.selectPeopleNote}>
                    <FormattedMessage id='BookingDatesForm.childrenNote' />
                  </div>
                </Fragment>
              ) : null}
            </div>
            {listingHasExtras && !isOnlyContact && (
              <div className={css.selectExtras}>
                <label>{extrasLabel}</label>
                <div className={css.additionalItem}>
                  {barbecueFee && (
                    <FieldCheckboxProduct
                      className={css.extraContainer}
                      id={`barbecueFee`}
                      label={barbecueFeeLabel}
                      name={'additionalItems'}
                      value={'barbecueFee'}
                      informationTooltipContent={barbecueExtraInformation}
                      price={formatMoney(intl, barbecueFee)}
                      symbol={0x1F356}
                    />
                  )}
                  {towelsFee && (
                    <FieldCheckboxProduct
                      className={css.extraContainer}
                      id={`towelsFee`}
                      label={towelsFeeLabel}
                      name={'additionalItems'}
                      value={'towelsFee'}
                      informationTooltipContent={towelsExtraInformation}
                      price={formatMoney(intl, towelsFee)}
                      symbol={0x1F9FA}
                    />
                  )}
                  {snackFee && (
                    <FieldCheckboxProduct
                      id={`snackFee`}
                      className={css.extraContainer}
                      label={snackFeeLabel}
                      name={'additionalItems'}
                      value={'snackFee'}
                      informationTooltipContent={snackExtraInformation}
                      price={formatMoney(intl, snackFee)}
                      symbol={0x1F964}
                    />
                  )}
                  {jacuzziFee && (
                    <FieldCheckboxProduct
                      className={css.extraContainer}
                      id={`jacuzziFee`}
                      label={jacuzziFeeLabel}
                      name={'additionalItems'}
                      value={'jacuzziFee'}
                      informationTooltipContent={jacuzziExtraInformation}
                      price={formatMoney(intl, jacuzziFee)}
                      symbol={0x1F6C1}
                    />
                  )}
                </div>
              </div>
            )}
            {isOnlyContact && (
              <FieldTextInput
                className={css.message}
                type='textarea'
                name='message'
                id={'message'}
                label={messageLabel}
                placeholder={messagePlaceholder}
                validate={messageRequired}
              />
            )}
            {!isOnlyContact && bookingInfo}
            {isOnlyContact ? (
              <div className={submitButtonClasses}>
                <PrimaryButton type='submit'>
                  <FormattedMessage id='ListingPage.contactTheUser' />
                </PrimaryButton>
              </div>
            ) : (
              <div className={submitButtonClasses}>
                <PrimaryButton type='submit'>
                  <FormattedMessage id='BookingDatesForm.requestToBook' />
                </PrimaryButton>
                {or}
                {contactUserButton}
              </div>
            )}
          </Form>
        );
      }}
    />
  );
};

BookingDatesFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  submitButtonWrapperClassName: null,
  price: null,
  bookingDatePlaceholder: null,
  timeSlots: null,
  maxCapacityToGuest: 0,
};

BookingDatesFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  submitButtonWrapperClassName: string,

  unitType: propTypes.bookingUnitType.isRequired,
  price: propTypes.money,
  maxCapacityToGuest: number,
  timeSlots: arrayOf(propTypes.timeSlot),

  // from injectIntl
  intl: intlShape.isRequired,

  // for tests
  bookingDatePlaceholder: string,
  authorDisplayName: string,
  isOnlyContact: bool,
  onSubmitEnquiry: func,
};

const BookingDatesForm = compose(injectIntl)(BookingDatesFormComponent);
BookingDatesForm.displayName = 'BookingDatesForm';

export default BookingDatesForm;



// WEBPACK FOOTER //
// ./src/forms/BookingDatesForm/BookingDatesForm.js